import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { InstitutionProvider } from './context/InstitutionContext';
import { AuthProvider } from './context/AuthContext';
import { RoleAssumptionProvider } from './context/RoleAssumptionContext';
import { ReportGenerationProvider } from './context/ReportGenerationContext';
import { ProtectedRoute, AdminRoute } from './components/auth';
import { FeatureGate } from './components/FeatureGate';
import { getFeatureConfig } from './config/features';
import MainLayout from './components/layout/MainLayout';
import Home from './pages/Home/Home';
import RoleLandingRouter from './components/landing/RoleLandingRouter';
import Ratings from './pages/Ratings/Ratings';
// RatingDetails removed - replaced by /ratings with InstitutionDashboard
// ReportPreviewPage removed - deprecated in favor of /reports/:reportId
import { ReportsDashboard, ReportPage } from './pages/Reports';
// Simulator removed - placeholder page not implemented
import AnalystBench, { ReportDrafter, BenchReportViewer } from './pages/AnalystBench';
import Agents from './pages/Agents/Agents';
import Settings from './pages/Settings/Settings';
// NetworkDemo removed - marketing demo page not used
import Profile from './pages/Profile/Profile';
import Login from './pages/Login/Login';
import PendingApproval from './pages/PendingApproval/PendingApproval';
import Suspended from './pages/Suspended/Suspended';
// RatingsMap is now integrated into Ratings page
import {
  AdminLayout,
  AdminDashboard,
  PendingUsers,
  UsersList,
  OrganizationsList,
  CreateOrganization,
  LicenseOverview,
  InventoryManagement,
  DataUpload,
} from './pages/admin';
import ReportsConfig from './pages/admin/reports/ReportsConfig';
import './styles/App.css';

const featureConfig = getFeatureConfig();

/**
 * Conditionally wraps children with RoleAssumptionProvider.
 * On-prem mode doesn't use role assumption, so we skip the provider.
 */
function MaybeRoleAssumption({ children }: { children: React.ReactNode }) {
  if (featureConfig.roleAssumption) {
    return <RoleAssumptionProvider>{children}</RoleAssumptionProvider>;
  }
  return <>{children}</>;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <MaybeRoleAssumption>
          <ReportGenerationProvider>
            <InstitutionProvider>
              <Router>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/pending" element={<PendingApproval />} />
                <Route path="/suspended" element={<Suspended />} />

                {/* Protected routes - require authentication */}
                {/* Home route - uses role-based landing pages */}
                {/* noPadding because bank users see full-bleed InstitutionDashboard */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout noPadding>
                        <RoleLandingRouter />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ratings"
                  element={
                    <ProtectedRoute>
                      <MainLayout noPadding>
                        <Ratings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
{/* /rating-details route removed - replaced by /ratings with InstitutionDashboard */}
{/* /report-preview route removed - deprecated in favor of /reports/:reportId */}
{/* /simulator route removed - placeholder page not implemented */}
                {/* Analyst Bench routes - new architecture without period in URL */}
                <Route
                  path="/analyst-bench"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <AnalystBench />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analyst-bench/:institutionId"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <AnalystBench />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                {/* Backward compatibility: redirect old URL format to new */}
                <Route
                  path="/analyst-bench/:institutionId/:period"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <AnalystBench />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analyst-bench/:institutionId/:period/report"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <ReportDrafter />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                {/* New Bench Report Viewer - for viewing reports within analyst bench */}
                <Route
                  path="/analyst-bench/:institutionId/reports/:reportId"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <BenchReportViewer />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agents"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Agents />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
{/* /network-demo route removed - marketing demo page not used */}
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Settings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Profile />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                {/* Reports routes - consolidated from /workflow */}
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <ReportsDashboard />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/:reportId"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <ReportPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes - require admin role */}
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <AdminLayout />
                    </AdminRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="users" element={<UsersList />} />
                  <Route path="inventory" element={<InventoryManagement />} />
                  {/* SaaS-only admin routes */}
                  <Route path="users/pending" element={
                    <FeatureGate feature="multiTenant" fallback={<AdminDashboard />}>
                      <PendingUsers />
                    </FeatureGate>
                  } />
                  <Route path="organizations" element={
                    <FeatureGate feature="multiTenant" fallback={<AdminDashboard />}>
                      <OrganizationsList />
                    </FeatureGate>
                  } />
                  <Route path="organizations/create" element={
                    <FeatureGate feature="multiTenant" fallback={<AdminDashboard />}>
                      <CreateOrganization />
                    </FeatureGate>
                  } />
                  <Route path="licenses" element={
                    <FeatureGate feature="licensing" fallback={<AdminDashboard />}>
                      <LicenseOverview />
                    </FeatureGate>
                  } />
                  <Route path="reports-config" element={
                    <FeatureGate feature="platformAdmin" fallback={<AdminDashboard />}>
                      <ReportsConfig />
                    </FeatureGate>
                  } />
                  <Route path="data-upload" element={
                    <FeatureGate feature="dataUpload" fallback={<AdminDashboard />}>
                      <DataUpload />
                    </FeatureGate>
                  } />
                </Route>
              </Routes>
              </Router>
            </InstitutionProvider>
          </ReportGenerationProvider>
        </MaybeRoleAssumption>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
